import { useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, Menu, X } from 'lucide-react';
import { ORG } from '../data/content';

const LINKS = [
  { label: 'Home', path: '/', id: 'home' },
  { label: 'Services', path: '/services', id: 'services' },
  { label: 'About', path: '/about', id: 'about' },
  { label: 'Case Studies', path: '/case-studies', id: 'case-studies' },
  { label: 'Insights', path: '/insights', id: 'insights' },
  { label: 'Contact', path: '/contact', id: 'contact' },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 border-b transition-all duration-300 ${
        scrolled ? 'border-brand-grey bg-white/95 shadow-[0_8px_30px_rgba(11,13,11,0.06)] backdrop-blur-md' : 'border-transparent bg-white/90 backdrop-blur-md'
      }`}
      data-testid="site-header"
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-3 lg:px-8">
        <Link to="/" className="flex flex-col items-start gap-1" data-testid="header-logo-link" aria-label="INSIGHTS STRATEGY — home">
          <img src="/assets/logo-header.webp" alt="INSIGHTS STRATEGY — Research, Strategy, Impact" className="h-auto w-[150px] sm:w-[200px] lg:w-[215px]" />
          <span className="text-[9px] font-medium uppercase tracking-[0.18em] text-brand-body">{ORG.division}</span>
        </Link>

        <nav className="hidden items-center gap-7 lg:flex" aria-label="Primary">
          {LINKS.map((l) => (
            <NavLink
              key={l.id}
              to={l.path}
              data-testid={`nav-link-${l.id}`}
              className={({ isActive }) =>
                `nav-link font-heading text-[13px] font-semibold tracking-wide transition-colors duration-200 ${
                  isActive ? 'nav-active text-brand-deepgreen' : 'text-brand-black/75 hover:text-brand-black'
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
          <Link
            to="/contact"
            data-testid="nav-cta-lets-talk"
            className="group flex items-center gap-2 rounded-full bg-brand-green px-5 py-2.5 font-heading text-[13px] font-bold text-white shadow-sm transition-colors duration-300 hover:bg-brand-deepgreen"
          >
            Let's Talk
            <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-0.5" />
          </Link>
        </nav>

        <button
          className="flex h-11 w-11 items-center justify-center rounded-full border border-brand-grey text-brand-black lg:hidden"
          onClick={() => setOpen(!open)}
          aria-label={open ? 'Close menu' : 'Open menu'}
          aria-expanded={open}
          data-testid="mobile-menu-toggle"
        >
          {open ? <X size={20} /> : <Menu size={20} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden border-t border-brand-grey bg-white lg:hidden"
            aria-label="Mobile"
            data-testid="mobile-menu"
          >
            <div className="flex flex-col px-6 py-4">
              {LINKS.map((l, i) => (
                <motion.div key={l.id} initial={{ opacity: 0, x: -12 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.05 * i }}>
                  <NavLink
                    to={l.path}
                    data-testid={`mobile-nav-link-${l.id}`}
                    className={({ isActive }) =>
                      `block border-b border-brand-grey/70 py-4 font-heading text-base font-semibold ${isActive ? 'text-brand-deepgreen' : 'text-brand-black'}`
                    }
                  >
                    {l.label}
                  </NavLink>
                </motion.div>
              ))}
              <Link
                to="/contact"
                data-testid="mobile-nav-cta-lets-talk"
                className="mt-4 mb-2 flex items-center justify-center gap-2 rounded-full bg-brand-green py-3.5 font-heading text-sm font-bold text-white"
              >
                Let's Talk <ArrowRight size={16} />
              </Link>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
